<template>
  <v-dialog :value="value" max-width="480" :persistent="saving" @input="!$event && close()">
    <v-card v-if="attendance">
      <v-card-title class="text-h6 font-weight-bold">¿Restaurar esta atención?</v-card-title>
      <v-card-text>
        <div class="info-grid mb-3">
          <div>
            <div class="pw-label">Mascota</div>
            <div class="pw-value">{{ attendance.petName }}</div>
          </div>
          <div>
            <div class="pw-label">Tutor</div>
            <div class="pw-value">{{ attendance.userDisplayName }}</div>
          </div>
          <div>
            <div class="pw-label">Fecha de atención</div>
            <div class="pw-value">{{ formatDate(attendance.attendedAt || attendance.createdAt) }}</div>
          </div>
          <div>
            <div class="pw-label">Monto</div>
            <div class="pw-value">{{ formatClp(attendance.pawerAmount) }}</div>
          </div>
        </div>
        <div v-if="attendance.cancellationReason" class="reason mb-3">
          Se anuló por: <strong>{{ attendance.cancellationReason }}</strong>
        </div>

        <div class="impact">{{ impactMessage }}</div>

        <v-alert v-if="errorMessage" type="error" dense outlined class="mt-3 mb-0">{{ errorMessage }}</v-alert>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text :disabled="saving" @click="close">Cancelar</v-btn>
        <v-btn color="primary" depressed :loading="saving" @click="confirm">Sí, restaurar</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import ClinicSettlementsService from '@/services/clinic-settlements.service';
import { formatClp, formatDate, settlementErrorMessage } from '@/utils/clinic-settlements.utils';

// Confirmación para deshacer una anulación hecha por error. Solo se ofrece cuando el
// backend la marca `restorable` (sin factura, o con la NC aún pendiente), así que el
// mensaje cubre esos dos casos. Emite `restored` con el detalle actualizado.
export default {
  name: 'RestoreAttendanceDialog',
  props: {
    value: { type: Boolean, default: false },
    clinicId: { type: String, default: '' },
    cutoffDate: { type: String, default: '' },
    attendance: { type: Object, default: null },
    settlement: { type: Object, default: null }
  },
  data() {
    return { saving: false, errorMessage: '' };
  },
  computed: {
    impactMessage() {
      if (!this.settlement || !this.attendance) return '';
      const current = this.settlement.pawerAmount || 0;
      const amount = this.attendance.pawerAmount || 0;
      if (!this.settlement.invoiceNumber) {
        return `La atención vuelve a contar: el monto total a liquidar a la clínica pasará de ${formatClp(
          current
        )} a ${formatClp(current + amount)}.`;
      }
      const remaining = Math.max((this.settlement.creditNoteAmount || 0) - amount, 0);
      if (!remaining) {
        return (
          `La factura ya no necesitará Nota de Crédito y volverá a Pendiente de Pago. ` +
          `El Monto Pawer se mantiene en ${formatClp(current)}.`
        );
      }
      return (
        `La Nota de Crédito pendiente bajará a ${formatClp(remaining)} por las demás atenciones anuladas. ` +
        `El Monto Pawer se mantiene en ${formatClp(current)}.`
      );
    }
  },
  watch: {
    value(open) {
      if (open) this.errorMessage = '';
    }
  },
  methods: {
    formatClp,
    formatDate,
    close() {
      if (this.saving) return;
      this.$emit('input', false);
    },
    async confirm() {
      this.saving = true;
      this.errorMessage = '';
      const res = await ClinicSettlementsService.restoreAttendance(
        this.clinicId,
        this.cutoffDate,
        this.attendance.invoiceId
      );
      this.saving = false;
      if (res.status !== 'ok') {
        this.errorMessage = settlementErrorMessage(res, 'No se pudo restaurar la atención. Intenta nuevamente.');
        return;
      }
      this.$emit('restored', res.data);
      this.$emit('input', false);
    }
  }
};
</script>

<style scoped>
.info-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px 16px;
}
.reason {
  font-size: 13px;
  color: var(--pw-text-muted);
}
.impact {
  padding: 12px 14px;
  border-radius: 10px;
  font-size: 13px;
  line-height: 1.45;
  background: var(--pw-surface-subtle);
  color: var(--pw-text);
}
</style>
